import { useEffect, useRef, useState, useCallback } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useUIStore, type SidebarTab } from '../../stores/uiStore';
import { useAnnotationStore } from '../../stores/annotationStore';
import { pdfEngine } from '../../core/PDFEngine';
import './Sidebar.css';

export function Sidebar() {
  const { currentPage, totalPages, setCurrentPage, zoom } = useDocumentStore();
  const { sidebarTab, setSidebarTab, sidebarWidth } = useUIStore();

  const tabs: { key: SidebarTab; icon: string; label: string }[] = [
    { key: 'thumbnails', icon: '📄', label: 'Pages' },
    { key: 'bookmarks', icon: '📑', label: 'Bookmarks' },
    { key: 'annotations', icon: '📝', label: 'Annotations' },
  ];

  return (
    <aside className="sidebar" style={{ width: sidebarWidth }}>
      <div className="sidebar-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`sidebar-tab ${sidebarTab === tab.key ? 'active' : ''}`}
            onClick={() => setSidebarTab(tab.key)}
            title={tab.label}
          >
            <span className="sidebar-tab-icon">{tab.icon}</span>
            <span className="sidebar-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="sidebar-content">
        {sidebarTab === 'thumbnails' && (
          <ThumbnailList
            currentPage={currentPage}
            totalPages={totalPages}
            onPageSelect={setCurrentPage}
            zoom={zoom}
          />
        )}
        {sidebarTab === 'bookmarks' && <BookmarkList onPageSelect={setCurrentPage} />}
        {sidebarTab === 'annotations' && <AnnotationList onPageSelect={setCurrentPage} />}
      </div>
    </aside>
  );
}

function ThumbnailList({
  currentPage,
  totalPages,
  onPageSelect,
}: {
  currentPage: number;
  totalPages: number;
  onPageSelect: (page: number) => void;
  zoom: number;
}) {
  const thumbnailRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());

  const renderThumbnail = useCallback(async (pageNum: number, canvas: HTMLCanvasElement) => {
    if (renderedPages.has(pageNum)) return;

    try {
      await pdfEngine.renderPage(pageNum, { scale: 0.25, rotation: 0, canvas });
      setRenderedPages((prev) => new Set([...prev, pageNum]));
    } catch {
      // Page might not be available yet
    }
  }, [renderedPages]);

  useEffect(() => {
    thumbnailRefs.current.forEach((canvas, pageNum) => {
      renderThumbnail(pageNum, canvas);
    });
  }, [totalPages, renderThumbnail]);

  return (
    <div className="thumbnail-list">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
        <button
          key={pageNum}
          className={`thumbnail-item ${pageNum === currentPage ? 'active' : ''}`}
          onClick={() => onPageSelect(pageNum)}
        >
          <canvas
            ref={(el) => {
              if (el) {
                thumbnailRefs.current.set(pageNum, el);
                renderThumbnail(pageNum, el);
              }
            }}
            className="thumbnail-canvas"
          />
          <span className="thumbnail-label">{pageNum}</span>
        </button>
      ))}
    </div>
  );
}

function BookmarkList({ onPageSelect }: { onPageSelect: (page: number) => void }) {
  const [outline, setOutline] = useState<any[] | null>(null);

  useEffect(() => {
    pdfEngine.getOutline().then(setOutline).catch(() => setOutline(null));
  }, []);

  if (!outline || outline.length === 0) {
    return (
      <div className="sidebar-empty">
        <span className="sidebar-empty-icon">📑</span>
        <p>No bookmarks found</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {outline.map((item, i) => (
        <div
          key={i}
          onClick={() => {
            if (typeof item.dest === 'number') {
              onPageSelect(item.dest);
            } else if (Array.isArray(item.dest)) {
              // PDF.js page dest mapping
              onPageSelect(1);
            }
          }}
          style={{
            padding: '6px 10px',
            fontSize: '13px',
            cursor: 'pointer',
            borderRadius: '4px',
            background: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-primary)',
          }}
        >
          📄 {item.title}
        </div>
      ))}
    </div>
  );
}

function AnnotationList({ onPageSelect }: { onPageSelect: (page: number) => void }) {
  const fabricCanvases = useAnnotationStore((s) => s.fabricCanvases);

  const items: { pageNumber: number; count: number; type: string }[] = [];
  fabricCanvases.forEach((canvas, pageNumber) => {
    if (canvas && canvas.getObjects().length > 0) {
      const objs = canvas.getObjects();
      items.push({
        pageNumber,
        count: objs.length,
        type: objs.map((o: any) => o.type).join(', '),
      });
    }
  });

  if (items.length === 0) {
    return (
      <div className="sidebar-empty">
        <span className="sidebar-empty-icon">📝</span>
        <p>No annotations yet</p>
        <p className="sidebar-empty-hint">Use the toolbar to add annotations</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map((item) => (
        <div
          key={item.pageNumber}
          onClick={() => onPageSelect(item.pageNumber)}
          style={{
            padding: '8px 12px',
            fontSize: '12px',
            cursor: 'pointer',
            borderRadius: '6px',
            background: 'var(--color-bg-tertiary)',
            border: '1px solid var(--color-surface-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <strong>Page {item.pageNumber}</strong>
            <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
              {item.type}
            </div>
          </div>
          <span
            style={{
              background: 'var(--color-accent-subtle)',
              color: 'var(--color-accent-text)',
              padding: '2px 6px',
              borderRadius: '10px',
              fontWeight: 'bold',
            }}
          >
            {item.count}
          </span>
        </div>
      ))}
    </div>
  );
}
